import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, CheckCircle, Loader2 } from "lucide-react";
import { API_URL, BRAND, ACTIVITIES, BRANCHES, waLink } from "@/data/site";

const inputCls =
  "w-full border border-white/12 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-zinc-500 transition-colors focus:border-red-500 focus:outline-none";
const labelCls = "mb-2 block font-mono2 text-[10px] uppercase tracking-[0.22em] text-zinc-400";

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SuccessPanel = ({ testid }) => (
  <div className="border border-red-600/40 bg-[#0F0F14] p-10 text-center" data-testid={testid}>
    <CheckCircle size={44} className="mx-auto text-red-500" />
    <h3 className="mt-6 font-display text-2xl font-black uppercase tracking-tight">Thank You!</h3>
    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
      Your information has been successfully submitted to New Boogie Woogie Academy. Our team will review your
      request and contact you using the details provided.
    </p>
    <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.28em] text-zinc-500">For faster assistance</p>
    <div className="mt-4 flex flex-col justify-center gap-3 sm:flex-row">
      <a href={BRAND.phoneLink} className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-rose-700 px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-white" data-testid={`${testid}-call`}>
        <Phone size={15} /> Call Now
      </a>
      <a href={waLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-emerald-500/50 px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-400 hover:bg-emerald-500/10" data-testid={`${testid}-whatsapp`}>
        <MessageCircle size={15} /> WhatsApp Us
      </a>
    </div>
  </div>
);

const ActivitySelect = ({ value, onChange, id, required = true }) => (
  <select id={id} value={value} onChange={onChange} required={required} className={`${inputCls} appearance-none`} data-testid={id}>
    <option value="" className="bg-[#0F0F14]">Select an activity</option>
    <optgroup label="Dance" className="bg-[#0F0F14]">
      {ACTIVITIES.filter((a) => a.cat === "Dance").map((a) => <option key={a.slug} value={a.name} className="bg-[#0F0F14]">{a.name}</option>)}
    </optgroup>
    <optgroup label="Fitness & Wellness" className="bg-[#0F0F14]">
      {ACTIVITIES.filter((a) => a.cat === "Fitness & Wellness").map((a) => <option key={a.slug} value={a.name} className="bg-[#0F0F14]">{a.name}</option>)}
    </optgroup>
    <optgroup label="Music & Creative Arts" className="bg-[#0F0F14]">
      {ACTIVITIES.filter((a) => a.cat === "Music & Creative Arts").map((a) => <option key={a.slug} value={a.name} className="bg-[#0F0F14]">{a.name}</option>)}
    </optgroup>
    <optgroup label="Performance & Personal Development" className="bg-[#0F0F14]">
      {ACTIVITIES.filter((a) => a.cat === "Performance & Personal Development").map((a) => <option key={a.slug} value={a.name} className="bg-[#0F0F14]">{a.name}</option>)}
    </optgroup>
    <optgroup label="Events & Entertainment" className="bg-[#0F0F14]">
      <option value="Riti Riwaz Event Planner" className="bg-[#0F0F14]">Riti Riwaz Event Planner</option>
      <option value="Birthday Party Entertainment" className="bg-[#0F0F14]">Birthday Party Entertainment</option>
      <option value="Mrs. Beauty Mom" className="bg-[#0F0F14]">Mrs. Beauty Mom</option>
    </optgroup>
  </select>
);

const Honeypot = ({ value, onChange }) => (
  <div className="absolute left-[-9999px] top-0" aria-hidden="true">
    <input type="text" name="company" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} />
  </div>
);

export const BookingForm = ({ defaultActivity = "" }) => {
  const empty = { name: "", phone: "", whatsapp: "", email: "", age: "", activity: defaultActivity, branch: "", date: "", time: "", message: "", company: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/bookings`, { ...form, email: form.email || null });
      setDone(true);
      toast.success("Booking request sent successfully.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <SuccessPanel testid="booking-success" />;

  return (
    <form onSubmit={submit} className="relative grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="booking-form">
      <Honeypot value={form.company} onChange={set("company")} />
      <Field label="Full Name" required>
        <input id="booking-name" className={inputCls} required value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="booking-name" />
      </Field>
      <Field label="Phone Number" required>
        <input id="booking-phone" className={inputCls} required type="tel" pattern="[0-9+ -]{10,15}" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" data-testid="booking-phone" />
      </Field>
      <Field label="WhatsApp Number">
        <input id="booking-whatsapp" className={inputCls} type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="If different from phone" data-testid="booking-whatsapp" />
      </Field>
      <Field label="Email">
        <input id="booking-email" className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="booking-email" />
      </Field>
      <Field label="Age">
        <input id="booking-age" className={inputCls} type="number" min="4" max="40" value={form.age} onChange={set("age")} placeholder="4 to 40 years" data-testid="booking-age" />
      </Field>
      <Field label="Activity" required>
        <ActivitySelect id="booking-activity" value={form.activity} onChange={set("activity")} />
      </Field>
      <Field label="Preferred Branch">
        <select id="booking-branch" className={`${inputCls} appearance-none`} value={form.branch} onChange={set("branch")} data-testid="booking-branch">
          <option value="" className="bg-[#0F0F14]">Select a branch</option>
          {BRANCHES.map((c) => <option key={c.id} value={c.name} className="bg-[#0F0F14]">{c.name}</option>)}
        </select>
      </Field>
      <Field label="Preferred Date">
        <input id="booking-date" className={`${inputCls} [color-scheme:dark]`} type="date" value={form.date} onChange={set("date")} data-testid="booking-date" />
      </Field>
      <Field label="Preferred Time">
        <input id="booking-time" className={inputCls} value={form.time} onChange={set("time")} placeholder="e.g. Evening, around 5 PM" data-testid="booking-time" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Message">
          <textarea id="booking-message" rows="4" className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything we should know?" data-testid="booking-message" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_0_24px_rgba(220,38,38,0.35)] transition-all hover:shadow-[0_0_38px_rgba(220,38,38,0.55)] disabled:opacity-60" data-testid="booking-submit">
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Submitting…" : "Book an Appointment"}
        </button>
        <p className="mt-3 text-center text-xs text-zinc-500">
          Appointments are confirmed by our team after review. No payment is taken online.
        </p>
      </div>
    </form>
  );
};

export const EnquiryForm = ({ compact = false }) => {
  const empty = { name: "", phone: "", whatsapp: "", email: "", age: "", activity: "", branch: "", contact_method: "", message: "", company: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/enquiries`, { ...form, email: form.email || null });
      setDone(true);
      toast.success("Enquiry sent successfully.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <SuccessPanel testid="enquiry-success" />;

  return (
    <form onSubmit={submit} className="relative grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="enquiry-form">
      <Honeypot value={form.company} onChange={set("company")} />
      <Field label="Full Name" required>
        <input id="enquiry-name" className={inputCls} required value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="enquiry-name" />
      </Field>
      <Field label="Phone Number" required>
        <input id="enquiry-phone" className={inputCls} required type="tel" pattern="[0-9+ -]{10,15}" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" data-testid="enquiry-phone" />
      </Field>
      <Field label="WhatsApp Number">
        <input id="enquiry-whatsapp" className={inputCls} type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="If different from phone" data-testid="enquiry-whatsapp" />
      </Field>
      <Field label="Email">
        <input id="enquiry-email" className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="enquiry-email" />
      </Field>
      <Field label="Age">
        <input id="enquiry-age" className={inputCls} type="number" min="4" max="40" value={form.age} onChange={set("age")} placeholder="4 to 40 years" data-testid="enquiry-age" />
      </Field>
      <Field label="Activity Interested In">
        <ActivitySelect id="enquiry-activity" value={form.activity} onChange={set("activity")} required={false} />
      </Field>
      <Field label="Preferred Branch">
        <select id="enquiry-branch" className={`${inputCls} appearance-none`} value={form.branch} onChange={set("branch")} data-testid="enquiry-branch">
          <option value="" className="bg-[#0F0F14]">Select a branch</option>
          {BRANCHES.map((c) => <option key={c.id} value={c.name} className="bg-[#0F0F14]">{c.name}</option>)}
        </select>
      </Field>
      <Field label="Preferred Contact Method">
        <select id="enquiry-contact-method" className={`${inputCls} appearance-none`} value={form.contact_method} onChange={set("contact_method")} data-testid="enquiry-contact-method">
          <option value="" className="bg-[#0F0F14]">Select</option>
          <option value="Call" className="bg-[#0F0F14]">Call</option>
          <option value="WhatsApp" className="bg-[#0F0F14]">WhatsApp</option>
          <option value="Email" className="bg-[#0F0F14]">Email</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Message">
          <textarea id="enquiry-message" rows={compact ? 3 : 4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us what you're looking for" data-testid="enquiry-message" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_0_24px_rgba(220,38,38,0.35)] transition-all hover:shadow-[0_0_38px_rgba(220,38,38,0.55)] disabled:opacity-60" data-testid="enquiry-submit">
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Sending…" : "Send Enquiry"}
        </button>
      </div>
    </form>
  );
};
